import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { sentenceCase } from 'change-case'
import { EllipsisVertical, Pencil, Snowflake, Sun, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { AWXCardStatus } from '@ankor-io/common/types/awx'
import type { Card, CardPayload } from '@ankor-io/common/types/cards'
import { formatCurrency } from '@ankor-io/common/util/currencies'

import { useCard } from '@/hooks/useCard'
import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import Loading from '@/ui-components/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'
import { CancelCardModal } from '@/views/components/CancelCardModal'
import { FreezeCardModal } from '@/views/components/FreezeCardModal'
import SimplePagination from '@/views/components/SimplePagination'
import { UnfreezeCardModal } from '@/views/components/UnfreezeCardModal'

const ITEMS_PER_PAGE = 100

type CardActionsDropdownProps = {
  card: CardPayload
  onUnfreeze: (cardId: string) => void
  onFreeze: (cardId: string) => void
  onCancel: (cardId: string) => void
  onEdit: (cardId: string) => void
}

const CardActionsDropdown = ({ card, onUnfreeze, onFreeze, onCancel, onEdit }: CardActionsDropdownProps) => {
  const isInactive = card.status === AWXCardStatus.INACTIVE
  const isClosed = card.status === AWXCardStatus.CLOSED

  if (isClosed) {
    return <span className="pl-3 text-gray-600 dark:text-gray-400">—</span>
  }

  return (
    <Dropdown>
      <DropdownButton
        plain
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded flex items-center gap-2"
      >
        <EllipsisVertical
          data-slot="icon"
          className="size-4"
        />
      </DropdownButton>
      <DropdownMenu anchor="bottom end">
        {/* Edit Card */}
        <DropdownItem
          onClick={() => onEdit(card.id)}
          data-card-status={card.status}
          data-action-type="edit-card"
        >
          <Pencil data-slot="icon" />
          Edit
        </DropdownItem>

        {/* Freeze/Unfreeze Card */}
        <DropdownItem
          onClick={() => (isInactive ? onUnfreeze(card.id) : onFreeze(card.id))}
          data-card-status={card.status}
          data-action-type={isInactive ? 'unfreeze-card' : 'freeze-card'}
        >
          {isInactive ? <Sun data-slot="icon" /> : <Snowflake data-slot="icon" />}
          {isInactive ? 'Unfreeze' : 'Freeze'}
        </DropdownItem>

        {/* Cancel Card */}
        <DropdownItem
          onClick={() => onCancel(card.id)}
          data-card-status={card.status}
          data-action-type="cancel-card"
        >
          <X data-slot="icon" />
          Cancel
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  )
}

export const CardsTable = () => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const navigate = useNavigate()

  const [isCancelCardModalOpen, setIsCancelCardModalOpen] = useState(false)
  const [isFreezeCardModalOpen, setIsFreezeCardModalOpen] = useState(false)
  const [isUnfreezeCardModalOpen, setIsUnfreezeCardModalOpen] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()
  const page = searchParams.get('page')
  const currentPage = page ? parseInt(page) : 1

  // Use the existing useCard hook for caching
  const { cards, isLoading, setCards, setLoading, setCard, hasCards } = useCard()

  const fetchCards = useCallback(async () => {
    if (!activeOrganizationId || !activeConnectedAccountId) {
      return
    }

    // Only fetch if we don't have cards
    if (hasCards()) {
      return // Use cached data
    }

    setLoading(true)

    try {
      const skip = (currentPage - 1) * ITEMS_PER_PAGE
      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/cards?take=${ITEMS_PER_PAGE}&skip=${skip}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!res.ok) {
        throw new Error('Failed to fetch cards')
      }

      const data: CardPayload[] = await res.json()
      setCards(data)
    } catch (err) {
      console.error('Error fetching cards:', err)
      setCards([])
    } finally {
      setLoading(false)
    }
  }, [activeOrganizationId, activeConnectedAccountId, currentPage, getAccessToken, setCards, setLoading, hasCards])

  useEffect(() => {
    if (activeOrganizationId && activeConnectedAccountId && !hasCards()) {
      fetchCards()
    }
  }, [activeOrganizationId, activeConnectedAccountId, hasCards, fetchCards])

  const handlePaginate = (newPage: number) => {
    setSearchParams({ page: newPage.toString() })
  }

  const handleEdit = (cardId: string) => {
    navigate(`/cards/card-edit/${cardId}`)
  }

  const handleFreeze = (cardId: string) => {
    setSelectedCardId(cardId)
    setIsFreezeCardModalOpen(true)
  }

  const handleUnfreeze = (cardId: string) => {
    setSelectedCardId(cardId)
    setIsUnfreezeCardModalOpen(true)
  }

  const handleCancel = (cardId: string) => {
    setSelectedCardId(cardId)
    setIsCancelCardModalOpen(true)
  }

  // Simplified card cache update
  const updateCardInCache = (updatedCard: Card) => {
    const existingCard = cards.find((card) => card.id === updatedCard.id)
    if (existingCard) {
      setCard({ ...existingCard, status: updatedCard.status })
    }
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <Table className="w-[calc(100vw-3rem)] md:w-auto">
        <TableHead>
          <TableRow>
            <TableHeader>Card</TableHeader>
            <TableHeader>User</TableHeader>
            <TableHeader>Limit</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {!cards || cards.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center py-8"
              >
                <p className="text-gray-600 dark:text-gray-400">No cards found</p>
              </TableCell>
            </TableRow>
          ) : (
            cards.map((card, idx) => (
              <TableRow
                key={idx}
                to={`/cards/card-details/${card.id}`}
              >
                <TableCell>{card.maskedCardNumber}</TableCell>
                <TableCell>{`${card.cardholder.firstName} ${card.cardholder.lastName}`}</TableCell>
                <TableCell>
                  {card.limits.map((limit) => (
                    <p key={limit.id}>
                      {`${formatCurrency(fromCents(limit.amountCents), card.currencyLimit)} - ${sentenceCase(limit.interval)}`}
                    </p>
                  ))}
                </TableCell>
                <TableCell>
                  <Badge
                    color={
                      card.status === AWXCardStatus.ACTIVE
                        ? 'green'
                        : card.status === AWXCardStatus.INACTIVE
                          ? 'yellow'
                          : 'red'
                    }
                  >
                    {sentenceCase(card.status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <CardActionsDropdown
                    card={card}
                    onEdit={handleEdit}
                    onFreeze={handleFreeze}
                    onUnfreeze={handleUnfreeze}
                    onCancel={handleCancel}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {cards.length > ITEMS_PER_PAGE && (
        <div className="flex justify-center mt-10">
          <SimplePagination
            totalPages={Math.ceil(cards.length / ITEMS_PER_PAGE)}
            currentPage={currentPage}
            onPaginate={handlePaginate}
          />
        </div>
      )}

      {selectedCardId && (
        <>
          <CancelCardModal
            onClose={() => {
              setIsCancelCardModalOpen(false)
              setSelectedCardId(null)
            }}
            isOpen={isCancelCardModalOpen}
            cardId={selectedCardId}
            onSuccess={updateCardInCache}
          />
          <FreezeCardModal
            onClose={() => {
              setIsFreezeCardModalOpen(false)
              setSelectedCardId(null)
            }}
            isOpen={isFreezeCardModalOpen}
            cardId={selectedCardId}
            onSuccess={updateCardInCache}
          />
          <UnfreezeCardModal
            onClose={() => {
              setIsUnfreezeCardModalOpen(false)
              setSelectedCardId(null)
            }}
            isOpen={isUnfreezeCardModalOpen}
            cardId={selectedCardId}
            onSuccess={updateCardInCache}
          />
        </>
      )}
    </>
  )
}

export default CardsTable
